/**
 * Client-authored shapes for the voice slice of the versioned REST
 * surface (`/api/sdk/v1/voice`): least-privilege Cartesia access tokens
 * for dictation (plan 027; the browser calls Cartesia directly without
 * seeing the server-held `CARTESIA_API_KEY`) and the HTTP TTS endpoint,
 * then live calls (plan 042): availability, the session mint, and the
 * recap a call saves into its chat. Dates are ISO 8601 strings (plain
 * JSON, no superjson).
 */

import type {
  MagnusUIMessage,
  VoiceSessionEndReason,
  VoiceSessionTurn,
} from "./messages";

/**
 * The one capability a minted token carries. Least-privilege: a `tts`
 * client must never receive an `stt` token and vice versa.
 */
export type SdkVoiceTokenGrant = "stt" | "tts";

/**
 * A short-lived Cartesia access token (the server requests a 10-minute
 * TTL; Cartesia caps at 1h). `cartesiaVersion` rides along so the
 * direct Cartesia call sends the server-pinned `Cartesia-Version`
 * header instead of a client-hardcoded one.
 */
export interface SdkVoiceToken {
  /** The `Cartesia-Version` header value for direct Cartesia calls. */
  cartesiaVersion: string;
  /** ISO 8601 timestamp after which Cartesia rejects the token. */
  expiresAt: string;
  token: string;
}

/**
 * `POST /api/sdk/v1/voice/tts` input — mirrors the zod bounds the
 * unversioned `/api/voice/tts` route accepts today (`text` 1–2000
 * chars). The response is binary `audio/mpeg`, not JSON, so there is
 * deliberately no response interface here.
 */
export interface SdkVoiceTtsRequest {
  text: string;
  /** Cartesia voice id; the server falls back to its default voice. */
  voiceId?: string;
}

/**
 * Live voice calls (plan 042) over `/api/sdk/v1/voice/live-session`. The
 * server builds the whole GPT-Live session config and exchanges the
 * browser's WebRTC offer with OpenAI; neither the key nor the config
 * reaches the client. Mirrors the first-party `voice.mintLiveSession`
 * procedure, which runs the same `LiveVoiceService` mint.
 */

/** Why live calls are off for the caller. */
export type SdkLiveVoiceUnavailableReason =
  /** The `chat.live-voice.enable` flag is off for this user. */
  | "disabled"
  /** The server has no `OPENAI_API_KEY`. */
  | "unconfigured";

/** `GET /api/sdk/v1/voice/live-session`: whether a call can be started. */
export interface SdkLiveVoiceAvailability {
  available: boolean;
  reason?: SdkLiveVoiceUnavailableReason;
}

/** One spoken turn carried into a replacement session (voice switch). */
export interface SdkLiveSessionHistoryTurn {
  role: "user" | "assistant";
  text: string;
}

/** `POST /api/sdk/v1/voice/live-session` body. */
export interface SdkLiveSessionRequest {
  /** The client's id for the whole call, kept across voice swaps; the
   *  recap is saved under it (`SdkVoiceSessionSaveInput.sessionId`). It
   *  holds the caller's one-call lease, so a swap renews it and only this
   *  call can release it. */
  callSessionId: string;
  /** The chat the call is bound to; titles the session's context. */
  chatId?: string;
  /** Present when replacing a running session (voice switch): the call so
   *  far, newest last. */
  history?: SdkLiveSessionHistoryTurn[];
  /** A `magnus_chat` request the swap interrupted, resumed by the new
   *  session's greeting. */
  interruptedQuery?: string;
  /** The browser's WebRTC offer. */
  sdp: string;
  /** IANA zone for the caller's local time in the instructions. */
  timeZone?: string;
  /** One of the live voices (`LIVE_VOICES`); unknown ids fall back. */
  voice?: string;
}

/** The minted session: the WebRTC answer plus the greeting to commentate. */
export interface SdkLiveSessionAnswer {
  greeting: string;
  sdp: string;
  sessionId: string;
}

/** `code` values the live-session route answers errors with. */
export type SdkLiveSessionErrorCode =
  | "BAD_REQUEST"
  | "CALL_IN_PROGRESS"
  | "CALL_LIMIT_REACHED"
  | "INTERNAL_ERROR"
  | "RATE_LIMITED"
  | "UPSTREAM_ERROR"
  | "UPSTREAM_RATE_LIMITED"
  | "VOICE_LIVE_DISABLED"
  | "VOICE_LIVE_UNCONFIGURED";

/** `DELETE /api/sdk/v1/voice/live-session?callSessionId=<id>`: hang up. */
export const LIVE_SESSION_RELEASE_PARAM = "callSessionId";

/**
 * `POST /api/sdk/v1/chats/<id>/voice-sessions` body: the recap of a
 * finished call. Idempotent on `sessionId`.
 */
export interface SdkVoiceSessionSaveInput {
  /** The GPT-Live session id. */
  callId?: string;
  /** Delete the chat instead when the user said nothing, it is still the
   *  untouched "Voice chat" the call created, and it has no rows. */
  discardIfEmpty?: boolean;
  endedAt: string;
  endReason: VoiceSessionEndReason;
  sessionId: string;
  startedAt: string;
  turns: VoiceSessionTurn[];
}

/** The saved recap row (`null` when nothing was said) and whether the
 *  empty chat was deleted instead. */
export interface SdkVoiceSessionSaved {
  discarded: boolean;
  message: MagnusUIMessage | null;
}
